import { useEffect, useMemo, useState } from "react";
import RichTextEditor from "./RichTextEditor";
import PdfPreviewModal from "./PdfPreviewModal";
import { sediList, buildSignatureText, downloadPdf } from "../lib/letterPdf";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Eye, Printer, Download } from "lucide-react";
import { toast } from "sonner";

const DEF_MARGINS = { top: 20, bottom: 15, left: 20, right: 15 };

// Editor identico a quello delle lettere: toolbar RichTextEditor + selettore intestazione + selettore firma
// + barra MARGINI MM + Anteprima (PDF.js) + Stampa + Scarica PDF. Riusa gli helper condivisi (no duplicazione).
export default function LetterComposer({ studio, value, onChange, fileName = "documento.pdf" }) {
  const sedi = useMemo(() => sediList(studio), [studio]);
  const [sedeKey, setSedeKey] = useState("principal");
  const [includeSig, setIncludeSig] = useState(false);
  const [margins, setMargins] = useState(DEF_MARGINS);
  const [letterPdfDoc, setLetterPdfDoc] = useState(null);

  useEffect(() => {
    const pm = studio?.settings?.print_margins;
    if (pm && typeof pm.top === "number") setMargins({ top: pm.top, bottom: pm.bottom, left: pm.left, right: pm.right });
  }, [studio]);

  const inte = useMemo(() => {
    if (sedeKey === "nessuna") return null;
    return sedi.find((x) => x.key === sedeKey) || sedi[0] || null;
  }, [sedi, sedeKey]);
  const withHead = sedeKey !== "nessuna" && !!inte;
  const signatureText = useMemo(() => buildSignatureText(studio), [studio]);
  const sigHtml = () => (includeSig && signatureText ? ("--\n" + signatureText).replace(/\n/g, "<br>") : "");

  const buildDoc = () => downloadPdf(fileName, value || "", withHead, inte, margins, { signature: sigHtml(), returnDoc: true });

  const doPreview = async () => {
    const t = toast.loading("Generazione anteprima…");
    try { setLetterPdfDoc(await buildDoc()); }
    catch (e) { toast.error("Anteprima non riuscita"); }
    finally { toast.dismiss(t); }
  };
  const doPrint = async () => {
    const t = toast.loading("Preparazione stampa…");
    try {
      const doc = await buildDoc();
      const w = window.open(doc.output("bloburl"), "_blank");
      if (!w) toast.error("Consenti i popup per aprire il PDF");
    } catch (e) { toast.error("Generazione PDF non riuscita"); }
    finally { toast.dismiss(t); }
  };
  const doDownload = () => {
    toast.promise(downloadPdf(fileName, value || "", withHead, inte, margins, { signature: sigHtml() }), {
      loading: "Generazione PDF…", success: "PDF scaricato", error: "Generazione PDF non riuscita",
    });
  };

  return (
    <div data-testid="letter-composer">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Select value={sedeKey} onValueChange={setSedeKey}>
          <SelectTrigger data-testid="composer-letterhead" className="w-56 h-9 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Sede Intestazione Stampa" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
            {sedi.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`composer-sede-opt-${s.key}`}>{s.label}</SelectItem>
            ))}
            <SelectItem value="nessuna" data-testid="composer-sede-opt-nessuna">Nessuna intestazione</SelectItem>
          </SelectContent>
        </Select>
        <Select value={includeSig ? "si" : "no"} onValueChange={(v) => setIncludeSig(v === "si")}>
          <SelectTrigger data-testid="composer-signature" className="w-48 h-9 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
            <SelectItem value="si">Con firma nel PDF</SelectItem>
            <SelectItem value="no">Senza firma</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" onClick={doPreview} data-testid="composer-preview" variant="outline" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800">
          <Eye className="h-4 w-4 mr-1" /> Anteprima
        </Button>
        <Button type="button" onClick={doPrint} data-testid="composer-print-btn" variant="outline" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800">
          <Printer className="h-4 w-4 mr-1" /> Stampa
        </Button>
        <Button type="button" onClick={doDownload} data-testid="composer-download" className="h-9 bg-cyan-600 hover:bg-cyan-700 text-white">
          <Download className="h-4 w-4 mr-1" /> Scarica PDF
        </Button>
      </div>
      <RichTextEditor value={value} onChange={onChange} onMarginsChange={setMargins} initialMargins={margins} withHead={withHead} letterhead={inte} />
      {letterPdfDoc && (
        <PdfPreviewModal doc={letterPdfDoc} filename={fileName} onClose={() => setLetterPdfDoc(null)} />
      )}
    </div>
  );
}
